import { useCallback, useMemo } from 'react';
import { useGetMeApi } from 'eds-common/api/v2';
import { useOrgHierarchy } from '@data/hooks/api/use-org-hierarchy-api/use-org-hierarchy-api';

/** Marks the admin's own organization; see `useOrgLabel`. */
export const OWN_ORG_SUFFIX = 'Teachers';

/**
 * Names an organization the way every report shows it.
 *
 * The admin's own organization holds educators directly as well as the
 * organizations beneath it, so once the hierarchy has more than one
 * organization in it that row is named for those educators — otherwise it
 * reads as the whole hierarchy it shares a name with. On its own there is
 * nothing to tell it apart from.
 */
export function useOrgLabel() {
  const { data: user } = useGetMeApi();
  const { data: orgs } = useOrgHierarchy();

  const currentOrgId = user?.currentOrg?.orgId;
  const hasHierarchy = (orgs ?? []).length > 1;

  return useCallback(
    (orgId: string, orgName: string) =>
      hasHierarchy && orgId === currentOrgId
        ? `${orgName} ${OWN_ORG_SUFFIX}`
        : orgName,
    [hasHierarchy, currentOrgId]
  );
}

/**
 * The organizations as every filter and heading names them.
 *
 * The one place the label is applied to the hierarchy, so the dropdown and the
 * layout cannot drift apart. The no-access message is the exception and keeps
 * the plain hierarchy: product access belongs to the whole organization, not
 * to its direct educators.
 *
 * Given an `appCode` it also marks which organizations hold that product. The
 * full hierarchy and the product-scoped one are two calls to the same
 * endpoint: the list must stay whole whatever the product, so a selection
 * survives a product change, and access is an attribute of a row rather than a
 * reason to drop it.
 */
export function useOrgOptions(appCode?: string) {
  const { data: orgs, isLoading } = useOrgHierarchy();
  const orgLabel = useOrgLabel();
  const { data: productOrgs, isLoading: isAccessLoading } = useOrgHierarchy(
    appCode ? { appCode } : {}
  );

  const data = useMemo(() => {
    // Until the product query answers, nothing is known to lack access —
    // better a tag that appears late than one that appears wrongly.
    const entitled = appCode && productOrgs ? productOrgs : undefined;
    const entitledIds = entitled && new Set(entitled.map((org) => org.orgId));

    return (orgs ?? []).map((org) => ({
      ...org,
      orgName: orgLabel(org.orgId, org.orgName),
      hasAccess: entitledIds ? entitledIds.has(org.orgId) : true,
    }));
  }, [orgs, orgLabel, appCode, productOrgs]);

  return {
    data,
    isLoading,
    isAccessLoading: Boolean(appCode) && isAccessLoading,
  };
}
